import { useEffect, useMemo, useState } from 'react';
import { Phone } from 'lucide-react';
import { trackExitClick, detectExitType } from '@/lib/trackExitClick';
import { APPOINTMENT_CONVENIOS, getAppointmentExamOptions } from '@/lib/appointmentForm';
import { recordAndStartPhoneCall } from '@/lib/contactTracking';
import FilterableSelect from '@/components/FilterableSelect';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

/**
 * Tracks regular exit links and collects the attendance context before a
 * telephone link is allowed to open. Rendered once by the public layout.
 */
export default function ExitClickTracker() {
  const [phoneLink, setPhoneLink] = useState(null);
  const [contact, setContact] = useState({ exame: '', convenio: '' });
  const examOptions = useMemo(() => getAppointmentExamOptions(contact.exame), [contact.exame]);

  useEffect(() => {
    const handleClick = (event) => {
      const link = event.target.closest('a');
      if (!link || !link.href) return;

      const href = link.href;
      const type = detectExitType(href);
      if (!type) return;

      const label = link.textContent?.trim() || link.getAttribute('aria-label') || '';
      if (type === 'telefone') {
        event.preventDefault();
        setPhoneLink({ href, label });
        setContact({ exame: String(link.dataset.exame || '').trim(), convenio: '' });
        return;
      }

      if (link.dataset.noTrack !== undefined) return;
      trackExitClick(href, label);
    };

    document.addEventListener('click', handleClick);
    return () => document.removeEventListener('click', handleClick);
  }, []);

  const closePhoneDialog = () => setPhoneLink(null);

  const confirmPhoneCall = (event) => {
    event.preventDefault();
    if (!phoneLink) return;

    // This is the only telephone tracking path. The programmatic navigation
    // below does not emit a second click event for the global listener.
    const started = recordAndStartPhoneCall({
      href: phoneLink.href,
      label: phoneLink.label,
      context: contact,
      track: trackExitClick,
      navigate: (destination) => window.location.assign(destination),
    });
    if (started) closePhoneDialog();
  };

  return (
    <Dialog open={Boolean(phoneLink)} onOpenChange={(open) => { if (!open) closePhoneDialog(); }}>
      <DialogContent
        className="w-[calc(100%-2rem)] max-w-md gap-0 border-4 border-cdi-blue bg-white p-0 shadow-[7px_7px_0_0_#FFBE0B] sm:rounded-none"
        aria-describedby="phone-contact-description"
      >
        <DialogHeader className="border-b-4 border-cdi-yellow bg-cdi-blue p-5 pr-14 text-left">
          <div className="mb-3 flex h-10 w-10 items-center justify-center bg-cdi-yellow text-cdi-blue">
            <Phone size={20} strokeWidth={2.5} aria-hidden="true" />
          </div>
          <DialogTitle className="text-xl font-black uppercase tracking-tight text-white">Antes de ligar</DialogTitle>
          <DialogDescription id="phone-contact-description" className="mt-1 text-sm font-medium leading-relaxed text-white/70">
            Informe o exame para que nossa equipe direcione melhor seu atendimento.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={confirmPhoneCall} className="space-y-5 p-5">
          <div>
            <label htmlFor="phone-contact-exam" className="mb-2 block text-[10px] font-black uppercase tracking-[0.16em] text-cdi-blue">
              Exame desejado *
            </label>
            <div className="relative">
              <FilterableSelect
                id="phone-contact-exam"
                required
                autoFocus
                value={contact.exame}
                options={examOptions}
                onValueChange={(exame) => setContact((previous) => ({ ...previous, exame }))}
                className="w-full appearance-none border-2 border-cdi-blue/20 bg-[#F7F7F3] px-4 py-3 pr-10 text-sm font-bold text-cdi-carbon outline-none transition focus:border-cdi-blue focus:bg-white focus:shadow-[3px_3px_0_0_#FFBE0B]"
                placeholder="Selecione ou digite o exame"
              />
            </div>
          </div>

          <div>
            <label htmlFor="phone-contact-insurance" className="mb-2 flex items-center justify-between text-[10px] font-black uppercase tracking-[0.16em] text-cdi-blue">
              <span>Convênio</span><span className="font-bold normal-case tracking-normal text-cdi-carbon/40">opcional</span>
            </label>
            <div className="relative">
              <FilterableSelect
                id="phone-contact-insurance"
                value={contact.convenio}
                options={APPOINTMENT_CONVENIOS}
                onValueChange={(convenio) => setContact((previous) => ({ ...previous, convenio }))}
                className="w-full appearance-none border-2 border-cdi-blue/20 bg-[#F7F7F3] px-4 py-3 pr-10 text-sm font-bold text-cdi-carbon outline-none transition focus:border-cdi-blue focus:bg-white focus:shadow-[3px_3px_0_0_#FFBE0B]"
                placeholder="Não informar agora"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-3 pt-1 sm:flex-row sm:justify-end">
            <button type="button" onClick={closePhoneDialog} className="border-2 border-cdi-blue px-4 py-3 text-xs font-black uppercase tracking-wider text-cdi-blue transition hover:bg-cdi-blue hover:text-white focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cdi-yellow">
              Cancelar
            </button>
            <button type="submit" className="bg-cdi-blue px-4 py-3 text-xs font-black uppercase tracking-wider text-white transition hover:bg-cdi-carbon focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-cdi-yellow">
              Ligar agora
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
